import { RowButton } from "@/components/ui/pressable";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, GitMerge, Lock } from "lucide-react";
import { toneClass } from "@/modules/shared/status";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
  type Identifier,
} from "ra-core";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import type { Deal, OrganizationMember, Ticket } from "@/modules/types";
import {
  buildTicketMergeFieldOverrides,
  buildTicketMergeRows,
  buildTicketMergeSnapshot,
  defaultTicketMergeFieldSides,
  TICKET_MERGE_STATUS_DOT,
  type TicketMergeFieldKey,
  type TicketMergeRow,
} from "@/modules/tickets/ticketMergeCompare";
import {
  ticketStatusLabel,
  ticketStatusVariant,
} from "@/modules/tickets/ticketInboxConfig";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  TicketMergeFlow,
  TicketMergeOutcome,
  TicketMergeSection,
  TicketMergeSteps,
} from "@/modules/tickets/TicketMergeReview";

const MUTABLE_FIELDS: TicketMergeFieldKey[] = [
  "requester_name",
  "requester_email",
  "phone",
  "subject",
  "status",
  "priority",
  "assignee_id",
  "company_id",
  "contact_id",
  "deal_id",
];

type TicketMergeCompareDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leftTicket: Ticket;
  rightTicket: Ticket;
  onMerged?: (primaryTicketId: Identifier) => void;
};

const useTicketMergeEnrichment = (ticket: Ticket) => {
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: ticket.contact_id ?? "" },
    { enabled: Boolean(ticket.contact_id) },
  );
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: ticket.company_id ?? "" },
    { enabled: Boolean(ticket.company_id) },
  );
  const { data: deal } = useGetOne<Deal>(
    "deals",
    { id: ticket.deal_id ?? "" },
    { enabled: Boolean(ticket.deal_id) },
  );
  return { contact, company, deal };
};

const MergeValueCell = ({
  value,
  selected,
  locked,
  onSelect,
}: {
  value: string;
  selected: boolean;
  locked?: boolean;
  onSelect: () => void;
}) => {
  if (locked) {
    return (
      <div className="flex h-full min-h-12 items-center px-5 py-3 text-sm text-muted-foreground">
        {value}
      </div>
    );
  }

  return (
    <RowButton
      layout="none"
      density="none"
      className={cn(
        "flex h-full min-h-12 w-full items-start gap-3 px-5 py-3 text-left text-sm transition-colors",
        selected
          ? "bg-primary/6 ring-1 ring-inset ring-primary/25"
          : "hover:bg-muted/50",
      )}
      onClick={onSelect}
    >
      <span
        className={cn(
          "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border shadow-sm",
          selected ? "border-primary bg-primary" : "border-input bg-background",
        )}
        aria-hidden
      >
        {selected ? (
          <span className="size-1.5 rounded-full bg-primary-foreground" />
        ) : null}
      </span>
      <span className="min-w-0 flex-1 break-words leading-snug">{value}</span>
    </RowButton>
  );
};

export const TicketMergeCompareDialog = ({
  open,
  onOpenChange,
  leftTicket,
  rightTicket,
  onMerged,
}: TicketMergeCompareDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [step, setStep] = useState<"compare" | "confirm">("compare");
  const [primarySide, setPrimarySide] = useState<"left" | "right">("left");
  const [fieldSides, setFieldSides] = useState<
    Record<TicketMergeFieldKey, "left" | "right">
  >(() => defaultTicketMergeFieldSides("left"));

  const leftEnrichment = useTicketMergeEnrichment(leftTicket);
  const rightEnrichment = useTicketMergeEnrichment(rightTicket);

  const { data: members = [] } = useGetList<OrganizationMember>(
    "organization_members",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "first_name", order: "ASC" },
    },
    { enabled: open },
  );

  const memberById = useMemo(
    () => new Map(members.map((member) => [String(member.id), member])),
    [members],
  );

  const leftSnapshot = useMemo(
    () =>
      buildTicketMergeSnapshot({
        ticket: leftTicket,
        contact: leftEnrichment.contact,
        company: leftEnrichment.company,
        dealName: leftEnrichment.deal?.name,
        assignee: leftTicket.assignee_id
          ? memberById.get(String(leftTicket.assignee_id))
          : null,
      }),
    [leftEnrichment, leftTicket, memberById],
  );

  const rightSnapshot = useMemo(
    () =>
      buildTicketMergeSnapshot({
        ticket: rightTicket,
        contact: rightEnrichment.contact,
        company: rightEnrichment.company,
        dealName: rightEnrichment.deal?.name,
        assignee: rightTicket.assignee_id
          ? memberById.get(String(rightTicket.assignee_id))
          : null,
      }),
    [memberById, rightEnrichment, rightTicket],
  );

  const rows = useMemo(
    () => buildTicketMergeRows(leftSnapshot, rightSnapshot),
    [leftSnapshot, rightSnapshot],
  );

  const primaryTicketId =
    primarySide === "left" ? leftTicket.id : rightTicket.id;
  const primaryTicket = primarySide === "left" ? leftTicket : rightTicket;
  const secondaryTicket = primarySide === "left" ? rightTicket : leftTicket;
  const secondaryTicketId = secondaryTicket.id;
  // Different accounts: almost always a mistake, so the confirm step warns.
  const differentClients =
    leftTicket.company_id != null &&
    rightTicket.company_id != null &&
    String(leftTicket.company_id) !== String(rightTicket.company_id);

  useEffect(() => {
    if (!open) return;
    setStep("compare");
    setPrimarySide("left");
    setFieldSides(defaultTicketMergeFieldSides("left"));
  }, [open, leftTicket.id, rightTicket.id]);

  const handlePrimaryChange = (side: "left" | "right") => {
    setPrimarySide(side);
    setFieldSides(defaultTicketMergeFieldSides(side));
  };

  const setAllFields = (side: "left" | "right", checked: boolean) => {
    if (!checked) return;
    setFieldSides(defaultTicketMergeFieldSides(side));
  };

  const mergeMutation = useMutation({
    mutationFn: () =>
      dataProvider.mergeTickets({
        primaryTicketId,
        mergeTicketIds: [secondaryTicketId],
        fieldOverrides: buildTicketMergeFieldOverrides(
          leftSnapshot,
          rightSnapshot,
          fieldSides,
        ),
      }),
    onSuccess: (result) => {
      notify(`Merged ticket into #${result.primary_ticket_id}`, {
        type: "success",
      });
      onOpenChange(false);
      refresh();
      onMerged?.(result.primary_ticket_id);
    },
    onError: (error: Error) => {
      notify(error.message || "Failed to merge tickets", { type: "error" });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !mergeMutation.isPending) {
      setStep("compare");
    }
    onOpenChange(nextOpen);
  };

  const columnHeader = (
    ticket: Ticket,
    side: "left" | "right",
    snapshot: typeof leftSnapshot,
  ) => {
    const isPrimary = primarySide === side;
    const allSelected = MUTABLE_FIELDS.every(
      (field) => fieldSides[field] === side,
    );

    return (
      <div
        className={cn(
          "flex h-full flex-col gap-3 border-l p-5",
          isPrimary && "bg-primary/[0.03]",
        )}
      >
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="radio"
            name="ticket-merge-primary"
            className="mt-1 size-4 accent-primary"
            checked={isPrimary}
            onChange={() => handlePrimaryChange(side)}
          />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">
                #{ticket.id}
              </span>
              {isPrimary ? (
                <Badge variant="secondary" className="h-5 px-2 text-2xs">
                  Primary
                </Badge>
              ) : null}
            </div>
            <p className="line-clamp-3 text-sm font-semibold leading-snug text-foreground">
              {snapshot.subject}
            </p>
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  "size-2 rounded-full",
                  TICKET_MERGE_STATUS_DOT[ticket.status] ??
                    "bg-muted-foreground/60",
                )}
              />
              <Badge
                variant={ticketStatusVariant(ticket.status)}
                className="h-5 px-2 text-2xs capitalize"
              >
                {ticketStatusLabel(ticket.status)}
              </Badge>
            </div>
          </div>
        </label>

        <label className="flex cursor-pointer items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => setAllFields(side, checked === true)}
          />
          Select all values
        </label>
      </div>
    );
  };

  const renderFieldRow = (row: TicketMergeRow) => {
    const fieldKey = row.key as TicketMergeFieldKey;
    const isMutable = !row.locked && MUTABLE_FIELDS.includes(fieldKey);

    return (
      <div
        key={row.key}
        className="grid grid-cols-[12rem_minmax(0,1fr)_minmax(0,1fr)]"
      >
        <div className="flex items-center gap-1.5 bg-muted/25 px-5 py-3 text-sm font-medium text-muted-foreground">
          {row.label}
          {row.locked ? <Lock className="size-3 opacity-50" /> : null}
        </div>

        <div className="border-l border-border">
          <MergeValueCell
            value={row.left}
            selected={isMutable && fieldSides[fieldKey] === "left"}
            locked={row.locked}
            onSelect={() =>
              setFieldSides((current) => ({ ...current, [fieldKey]: "left" }))
            }
          />
        </div>

        <div className="border-l border-border">
          <MergeValueCell
            value={row.right}
            selected={isMutable && fieldSides[fieldKey] === "right"}
            locked={row.locked}
            onSelect={() =>
              setFieldSides((current) => ({ ...current, [fieldKey]: "right" }))
            }
          />
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={!mergeMutation.isPending}
        className="flex max-h-[min(92vh,900px)] w-[min(96vw,80rem)] max-w-[min(96vw,80rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(96vw,80rem)]"
      >
        <DialogHeader className="shrink-0 space-y-1 border-b px-6 py-4 text-left">
          <DialogTitle className="text-base">Merge tickets</DialogTitle>
          <DialogDescription>
            {step === "compare"
              ? "Choose the primary ticket, then pick which value to keep for each field."
              : `Check the result before merging #${secondaryTicketId} into #${primaryTicketId}.`}
          </DialogDescription>
          <TicketMergeSteps
            steps={["Choose values", "Review"]}
            current={step === "compare" ? 0 : 1}
          />
        </DialogHeader>

        {step === "compare" ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <div className="overflow-hidden rounded-xl border border-border bg-background shadow-sm">
              <div className="grid grid-cols-[12rem_minmax(0,1fr)_minmax(0,1fr)] border-b border-border bg-muted/35">
                <div className="px-5 py-3.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  Field
                </div>
                {columnHeader(leftTicket, "left", leftSnapshot)}
                {columnHeader(rightTicket, "right", rightSnapshot)}
              </div>

              <div className="divide-y divide-border">
                {rows.map(renderFieldRow)}
              </div>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
            {differentClients ? (
              <div
                role="alert"
                className={cn(
                  "flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm",
                  toneClass("warning", "border"),
                )}
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <p>
                  <span className="font-medium">
                    These tickets belong to different clients
                  </span>{" "}
                  ({leftEnrichment.company?.name ?? `#${leftTicket.company_id}`}{" "}
                  and{" "}
                  {rightEnrichment.company?.name ??
                    `#${rightTicket.company_id}`}
                  ). Merging moves #{secondaryTicketId}&apos;s invoice and files
                  onto #{primaryTicketId}. Separating the tickets later puts
                  them back.
                </p>
              </div>
            ) : null}
            <TicketMergeFlow
              primary={primaryTicket}
              secondaries={[secondaryTicket]}
            />

            <TicketMergeSection title={`Result on #${primaryTicketId}`}>
              <div className="divide-y divide-border">
                {rows
                  .filter((row) => !row.locked)
                  .map((row) => {
                    const side = fieldSides[row.key as TicketMergeFieldKey];
                    const fromSecondary = side !== primarySide;
                    return (
                      <div
                        key={row.key}
                        className="grid grid-cols-[12rem_minmax(0,1fr)]"
                      >
                        <div className="flex items-center bg-muted/25 px-5 py-3 text-sm font-medium text-muted-foreground">
                          {row.label}
                        </div>
                        <div className="flex items-start gap-2 border-l border-border px-5 py-3 text-sm">
                          <span className="min-w-0 flex-1 break-words leading-snug">
                            {side === "left" ? row.left : row.right}
                          </span>
                          {fromSecondary ? (
                            <Badge
                              variant="outline"
                              className="h-5 shrink-0 px-2 text-2xs"
                            >
                              From #{secondaryTicketId}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </TicketMergeSection>

            <TicketMergeOutcome
              items={[
                `All messages from #${secondaryTicketId} move into #${primaryTicketId}.`,
                `#${secondaryTicketId} is marked resolved, hidden from the inbox and stays linked for reference.`,
              ]}
            />
          </div>
        )}

        <DialogFooter className="shrink-0 gap-2 border-t bg-muted/15 px-6 py-4">
          {step === "compare" ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={() => setStep("confirm")}>
                <GitMerge className="size-4" />
                Continue
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={mergeMutation.isPending}
                onClick={() => setStep("compare")}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={mergeMutation.isPending}
                onClick={() => mergeMutation.mutate()}
              >
                <GitMerge className="size-4" />
                Yes, merge
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
